import { AgeRange } from '@/catalog/types/age-range';
import { CreditScore } from '@/catalog/types/credit-score';
import { LoaderButton } from '@/common/components/loader-button';
import Spacer from '@/common/components/spacer';
import { fullstoryEvent } from '@/fullstory/utils/fullstory-event';
import HelpSmall from '@/icons/help-small';
import { Checkbox } from '@/shadcn-ui/checkbox';
import { Label } from '@/shadcn-ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/shadcn-ui/select';
import { colors } from '@/theme/colors';
import { digiohCreditLocalStore } from '@/util-react-router/digioh-credit-local-store';
import { Form } from 'react-router';
import { useState } from 'react';

export interface StepCreditAgeProps {
  selectedCreditScoreId?: string;
  selectedIsOver25?: boolean;
  creditScores: CreditScore[];
  selectedAgeRangeId?: string;
  ageRanges?: AgeRange[];
  // Should be true if the drawer is opened from the inventory page when the user is prompted to enter zipcode and credit score
  isOpenedFromInventory: boolean;
}

export default function StepCreditAge({
  selectedCreditScoreId,
  selectedIsOver25,
  creditScores,
  selectedAgeRangeId,
  ageRanges,
  isOpenedFromInventory = false,
}: StepCreditAgeProps) {
  const [isOver25, setIsOver25] = useState(selectedIsOver25);
  const [ageRangeId, setAgeRangeId] = useState(selectedAgeRangeId);
  const [creditScoreId, setCreditScoreId] = useState(selectedCreditScoreId);

  const shouldUseAgeRange = ageRanges && ageRanges.length > 0;
  const disabled = (shouldUseAgeRange ? !ageRangeId : false) || !creditScoreId;

  const onSubmit = () => {
    const ageRangeOver25 = isOver25 ? '25+' : '<25';
    const ageRangeRangeId = ageRangeId ?? 'unk';

    fullstoryEvent('personalize_rates_credit_score_selected', {
      personalize_rates_selected_credit_score: creditScoreId ?? 'unk',
      age_range: shouldUseAgeRange ? ageRangeRangeId : ageRangeOver25,
    });
    digiohCreditLocalStore(creditScoreId ?? 'unk');
  };

  return (
    <Form method="post">
      <div className="flex flex-col gap-[16px] md:flex-row">
        <div className="w-full md:w-1/3">
          <p className="body2">Age</p>
          <Spacer variant="height4" />
          {!shouldUseAgeRange ? (
            <div className="mt-[16px] flex items-center">
              <Checkbox
                checked={isOver25}
                id="over25"
                name="over25"
                onCheckedChange={() => setIsOver25((prev) => !prev)}
              />
              <Spacer variant="width8" />
              <Label htmlFor="over25" className="cursor-pointer text-base">
                25+ years old
              </Label>
            </div>
          ) : null}
          {shouldUseAgeRange ? (
            <>
              <Select
                defaultValue={selectedAgeRangeId}
                onValueChange={(e) => setAgeRangeId(e)}
                name="age"
              >
                <SelectTrigger className="h-[56px] px-[16px] py-[12px]">
                  <p className="body">
                    {ageRangeId
                      ? ageRanges.find((a) => a.id === ageRangeId)?.display_name
                      : 'Select one'}
                  </p>
                </SelectTrigger>

                <SelectContent>
                  {ageRanges.map((ar) => (
                    <SelectItem key={ar.id} value={ar.id}>
                      {ageRanges.find((a) => a.id === ar.id)?.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          ) : null}
        </div>
        <div className="w-full md:w-2/3">
          <p className="body2">Credit score</p>
          <Spacer variant="height4" />
          <Select
            defaultValue={selectedCreditScoreId}
            onValueChange={(e) => setCreditScoreId(e)}
            name="credit"
          >
            <SelectTrigger className="h-[56px] px-[16px] py-[12px]">
              <p className="body">
                {creditScoreId
                  ? `${creditScores.find((c) => c.id === creditScoreId)?.range} (${creditScores.find((c) => c.id === creditScoreId)?.name})`
                  : 'Select one'}
              </p>
            </SelectTrigger>

            <SelectContent>
              {creditScores.map((cs) => (
                <SelectItem key={cs.id} value={cs.id}>
                  {creditScores.find((c) => c.id === cs.id)?.range} (
                  {creditScores.find((c) => c.id === cs.id)?.name})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Spacer variant="height16" />

      <div className="flex flex-row gap-[8px] rounded-[16px] bg-flexcar-blue90 px-[16px] py-[12px]">
        <HelpSmall
          fillPrimary={colors.neutral1}
          className="relative top-[2px] flex-shrink-0"
        />

        <p className="body2">
          <span className="body2-strong">Don’t know your score?</span> Just take a guess
          and we&apos;ll confirm later at checkout.
        </p>
      </div>
      <Spacer variant="height24" />
      <LoaderButton
        className="w-full"
        type="submit"
        name="intent"
        value="add_credit_and_age"
        disabled={disabled}
        onClick={onSubmit}
      >
        {isOpenedFromInventory ? 'View cars' : 'Update'}
      </LoaderButton>
    </Form>
  );
}
